import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDateTime } from "@/lib/format";
import { DateFilter, buildParams } from "@/components/DateFilter";
import { printThermal, printA4 } from "@/lib/print";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, ClipboardList, IndianRupee, Printer, FileText } from "lucide-react";
import { toast } from "sonner";

function toSaleLike(p) {
  return {
    bill_no: p.invoice_no || "PURCHASE", customer_name: p.vendor_name || "Vendor",
    items: (p.items || []).map((it) => ({ name: it.name, qty: it.qty, price: it.purchase_price, discount: 0, tax_rate: it.tax_rate, line_total: it.line_total, tax_amount: it.tax_amount })),
    subtotal: p.subtotal, tax_total: p.tax_total, bill_discount: 0, grand_total: p.grand_total,
    payment_method: p.payment_method, paid_amount: p.paid_amount, outstanding: p.outstanding, created_at: p.created_at,
  };
}

export default function PurchaseHistory() {
  const qc = useQueryClient();
  const { hasPerm } = useAuth();
  const [filter, setFilter] = useState({ period: "this_month" });
  const [search, setSearch] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState("");

  const { data: purchases } = useQuery({
    queryKey: ["purchases", filter, search],
    queryFn: async () => (await api.get("/purchases", { params: { ...buildParams(filter), search } })).data,
  });
  const { data: settings } = useQuery({ queryKey: ["all-settings"], queryFn: async () => (await api.get("/settings")).data });
  const bizLabel = { ...(settings?.business || {}), business_name: (settings?.business?.business_name || "Purchase Bill") };

  const total = (purchases || []).reduce((a, b) => a + (b.grand_total || 0), 0);
  const outstanding = (purchases || []).reduce((a, b) => a + (b.outstanding || 0), 0);

  async function pay() {
    try {
      await api.post(`/purchases/${payFor.id}/payment`, { amount: Number(payAmt) });
      toast.success("Payment recorded");
      setPayFor(null); setPayAmt("");
      qc.invalidateQueries({ queryKey: ["purchases"] });
    } catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">Purchase History</h2>
          <p className="text-slate-500 text-sm">{(purchases || []).length} purchases · {money(total)} · Outstanding {money(outstanding)}</p></div>
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      <Card className="p-4 border-slate-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice / vendor..." className="pl-9" data-testid="purchase-search" />
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Invoice</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {(purchases || []).length ? purchases.map((p) => (
                <TableRow key={p.id} className="hover:bg-slate-50">
                  <TableCell className="font-mono">{p.invoice_no || "—"}</TableCell>
                  <TableCell>{p.vendor_name || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDateTime(p.created_at)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{money(p.grand_total)}</TableCell>
                  <TableCell className="text-right font-mono">{money(p.paid_amount)}</TableCell>
                  <TableCell><Badge className={p.payment_status === "paid" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : p.payment_status === "partial" ? "bg-amber-50 text-amber-700 hover:bg-amber-50" : "bg-rose-50 text-rose-700 hover:bg-rose-50"}>{p.payment_status}</Badge></TableCell>
                  <TableCell className="text-right"><div className="flex justify-end gap-1">
                    {hasPerm("print") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => printThermal(toSaleLike(p), bizLabel, settings?.bill, settings?.printer)} data-testid={`print-purchase-${p.id}`}><Printer className="h-4 w-4 text-slate-500" /></Button>}
                    {hasPerm("pdf_export") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => printA4(toSaleLike(p), bizLabel, settings?.bill)} data-testid={`pdf-purchase-${p.id}`}><FileText className="h-4 w-4 text-[#1A2A5E]" /></Button>}
                    {p.outstanding > 0 && <Button size="sm" variant="outline" onClick={() => { setPayFor(p); setPayAmt(String(p.outstanding)); }} data-testid={`pay-purchase-${p.id}`}><IndianRupee className="h-3.5 w-3.5 mr-1" />Pay</Button>}
                  </div></TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400"><ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />No purchases</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!payFor} onOpenChange={() => setPayFor(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-500">Outstanding: <span className="font-mono font-semibold text-rose-600">{money(payFor?.outstanding)}</span></div>
            <div><Label>Amount</Label><Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="mt-1 font-mono" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayFor(null)}>Cancel</Button><Button className="bg-[#1A2A5E]" onClick={pay}>Save Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
